// NoteFragments — 5 floating note boxes that drift around the planet on
// a sphere of radius ~4.4-4.8 (per planetSections.NOTES). Each fragment
// is a small warm-white BoxGeometry in a local billboard group that
// faces the camera, with a random tilt and a gentle Y-axis bob.
//
// Per the spec, all fragments live inside the world group so they
// rotate with the planet (slowly, as a single group rotation).
//
// Interaction: R3F onPointerOver/Out/Click handlers update
// window.__planetHover and window.__planetOpenPanelId and dispatch the
// `note:open` event the NoteDetailCard listens for.

import { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import type { Group, Mesh } from 'three';

import { NOTES, type NoteConfig, type Vec3 } from './planetSections';
import { openDetail, registerPickables } from './SatelliteInteraction';

// ---------------------------------------------------------------------------
// Tunables
// ---------------------------------------------------------------------------

const HOVER_SCALE = 1.2;
const BASE_EMISSIVE = 0.15;
const HOVER_EMISSIVE = 0.45;
const BOB_AMP = 0.05; // amplitude of the Y bob
const BOB_FREQ = 0.5; // angular frequency of the bob (rad/sec)
const GROUP_ROT_SPEED = 0.015; // radians/sec, very slow drift
// Notes past this sphere radius are simplified: we drop the decorative
// gold spine strip and the gold-leaf emissive on the body to save a
// mesh + a material at the outer rim of the orbit where the spine
// reads as a tiny one-pixel sliver anyway.
const FAR_SIMPLIFY_RADIUS = 4.5;

// ---------------------------------------------------------------------------
// Per-fragment runtime scratch
// ---------------------------------------------------------------------------

interface FragmentRuntime {
  hovered: boolean;
  /** Eased scale (1 -> HOVER_SCALE on hover). */
  scale: number;
  /** Bob phase, randomized so notes don't bob in sync. */
  bobPhase: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert (theta, phi) on a sphere of radius r to a 3D position.
 *  theta is longitude (0..2π), phi is latitude (-π/2..π/2). */
function spherePos(
  r: number,
  theta: number,
  phi: number,
  out: THREE.Vector3,
): void {
  const cp = Math.cos(phi);
  out.set(
    r * cp * Math.cos(theta),
    r * Math.sin(phi),
    r * cp * Math.sin(theta),
  );
}

/** Stable hash → pseudo-random number in [0,1). */
function hash(n: number): number {
  let x = (n * 2654435761) >>> 0;
  x ^= x >>> 13;
  x = Math.imul(x, 0x5bd1e995) >>> 0;
  x ^= x >>> 15;
  return (x >>> 0) / 4294967296;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface NoteFragmentsProps {
  /** Show titles on hover (spec). Always-faint label otherwise. */
  showLabelsOnHover?: boolean;
}

export function NoteFragments({ showLabelsOnHover = true }: NoteFragmentsProps) {
  const camera = useThree((s) => s.camera);
  const groupRef = useRef<Group | null>(null);
  const meshRefs = useRef<(Mesh | null)[]>([]);
  const billboardRefs = useRef<(Group | null)[]>([]);
  const runtimesRef = useRef<FragmentRuntime[]>([]);
  const labelOpacityRefs = useRef<number[]>([]);

  // Initialize runtimes + per-note tilt + bob phases once.
  const noteConfigs = useMemo(() => {
    if (runtimesRef.current.length !== NOTES.length) {
      runtimesRef.current = NOTES.map((_, i) => ({
        hovered: false,
        scale: 1,
        bobPhase: hash(i + 1) * Math.PI * 2,
      }));
      labelOpacityRefs.current = NOTES.map(() => 0);
    }
    return NOTES.map((note, i) => {
      // Random tilt (radians) per axis, in ±0.4 rad range.
      const tilt: Vec3 = [
        (hash(i + 11) - 0.5) * 0.8,
        (hash(i + 23) - 0.5) * 0.8,
        (hash(i + 37) - 0.5) * 0.8,
      ];
      // Spin axis (unit). Use the config's spinAxis normalized, falling
      // back to (0,1,0).
      const [ax, ay, az] = note.spinAxis;
      const len = Math.hypot(ax, ay, az) || 1;
      const spinAxis = new THREE.Vector3(ax / len, ay / len, az / len);
      const baseTilt = (hash(i + 100) - 0.5) * 0.4;
      return { note, tilt, spinAxis, baseTilt };
    });
  }, []);

  const scratch = useMemo(
    () => ({
      basePos: new THREE.Vector3(),
      tmpColor: new THREE.Color(),
    }),
    [],
  );

  // Register meshes with the raycaster on mount, unregister on unmount.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const raf = requestAnimationFrame(() => {
      const meshes = meshRefs.current
        .filter((m): m is Mesh => !!m)
        .map((mesh, i) => ({
          mesh,
          kind: 'note' as const,
          id: NOTES[i].id,
        }));
      if (meshes.length > 0) registerPickables(meshes);
    });
    return () => {
      cancelAnimationFrame(raf);
      const meshes = meshRefs.current
        .filter((m): m is Mesh => !!m)
        .map((mesh, i) => ({
          mesh,
          kind: 'note' as const,
          id: NOTES[i].id,
        }));
      if (meshes.length > 0 && typeof window !== 'undefined') {
        const w = window as Window & {
          __planetPickables?: { mesh: Mesh; kind: string; id: string | number }[];
        };
        if (w.__planetPickables) {
          w.__planetPickables = w.__planetPickables.filter(
            (p) => !(p.kind === 'note' && meshes.some((m) => m.id === p.id)),
          );
        }
      }
    };
  }, []);

  // -------------------------------------------------------------------------
  // Frame loop: slow group rotation, bob, scale ease, emissive.
  // -------------------------------------------------------------------------
  useFrame((state, delta) => {
    const dt = Math.min(delta, 1 / 30);
    const t = state.clock.elapsedTime;

    // Slow drift of the whole fragment group. Independent of the
    // planet rotation, so notes feel like they have their own weather.
    if (groupRef.current) {
      groupRef.current.rotation.y += dt * GROUP_ROT_SPEED;
      groupRef.current.rotation.x = Math.sin(t * 0.07) * 0.08;
    }

    for (let i = 0; i < noteConfigs.length; i++) {
      const cfg = noteConfigs[i];
      const rt = runtimesRef.current[i];
      const billboard = billboardRefs.current[i];
      const mesh = meshRefs.current[i];
      if (!mesh || !billboard || !rt) continue;

      // Position from config (in group's local space).
      spherePos(
        cfg.note.positionOnOrbit.sphereRadius,
        cfg.note.positionOnOrbit.theta,
        cfg.note.positionOnOrbit.phi,
        scratch.basePos,
      );

      // Y-axis bob: sin(t * freq + phase) * amp.
      const bob = Math.sin(t * BOB_FREQ + rt.bobPhase) * BOB_AMP;
      scratch.basePos.y += bob;
      billboard.position.copy(scratch.basePos);

      // Lightweight billboard: face the active R3F camera, then add a
      // small deterministic tilt/spin for visual variety. This replaces
      // Drei's <Billboard>, avoiding the drei hook path that could crash
      // with undefined dependency arrays in production chunks.
      billboard.lookAt(camera.position);
      billboard.rotateX(cfg.tilt[0] + cfg.baseTilt);
      billboard.rotateY(cfg.tilt[1] + t * cfg.note.spinSpeed * 0.5);
      billboard.rotateZ(cfg.tilt[2]);

      // Scale ease.
      const targetScale = rt.hovered ? HOVER_SCALE : 1;
      rt.scale += (targetScale - rt.scale) * Math.min(1, 6 * dt);
      mesh.scale.setScalar(rt.scale * cfg.note.scale);

      // Emissive on hover.
      const mat = mesh.material as THREE.MeshStandardMaterial | undefined;
      if (mat) {
        scratch.tmpColor.set('#f5f0e8');
        mat.color.copy(scratch.tmpColor);
        const e = rt.hovered ? HOVER_EMISSIVE : BASE_EMISSIVE;
        mat.emissive.copy(scratch.tmpColor).multiplyScalar(e);
        mat.emissiveIntensity = 1;
      }

      // Label opacity — fade in on hover. Stored on the billboard's
      // userData for the future CSS2D overlay path.
      if (showLabelsOnHover) {
        const target = rt.hovered ? 1 : 0;
        const cur = labelOpacityRefs.current[i] ?? 0;
        const next = cur + (target - cur) * Math.min(1, 6 * dt);
        labelOpacityRefs.current[i] = next;
        billboard.userData.labelOpacity = next;
      }
    }
  });

  // -------------------------------------------------------------------------
  // Pointer handlers — same pattern as Satellites.
  // -------------------------------------------------------------------------
  const handlePointerOver = (note: NoteConfig, i: number) =>
    (e: { stopPropagation: () => void }) => {
      e.stopPropagation();
      const rt = runtimesRef.current[i];
      if (rt) rt.hovered = true;
      if (typeof window !== 'undefined') {
        window.__planetHover = { kind: 'note', id: note.id };
      }
      document.body.style.cursor = 'pointer';
    };

  const handlePointerOut = (note: NoteConfig, i: number) =>
    (e: { stopPropagation: () => void }) => {
      e.stopPropagation();
      const rt = runtimesRef.current[i];
      if (rt) rt.hovered = false;
      if (typeof window !== 'undefined') {
        const cur = window.__planetHover;
        if (cur && cur.kind === 'note' && cur.id === note.id) {
          window.__planetHover = null;
        }
      }
      document.body.style.cursor = '';
    };

  const handleClick = (note: NoteConfig, i: number) =>
    (e: { stopPropagation: () => void }) => {
      e.stopPropagation();
      // Exploration mode: fly to the note, show info card.
      if (typeof window !== 'undefined' && window.__planetMode === 'exploration') {
        const mesh = meshRefs.current[i];
        if (mesh && window.__planetShowInfoCard) {
          const wp = new THREE.Vector3();
          mesh.getWorldPosition(wp);
          window.__planetShowInfoCard({
            kind: 'note',
            id: note.id,
            title: note.title,
            blurb: note.excerpt ?? '',
            worldPos: [wp.x, wp.y, wp.z],
          });
        }
        return;
      }
      if (typeof window !== 'undefined') {
        window.__planetOpenPanelId = `note:${note.id}`;
        // Open the deep-dive detail panel (note).
        openDetail({ type: 'note', id: String(note.id) });
        // Also dispatch the legacy events for the right-rail card.
        window.dispatchEvent(
          new CustomEvent('planet:open-panel', { detail: `note:${note.id}` }),
        );
        window.dispatchEvent(
          new CustomEvent('note:open', { detail: { id: note.id } }),
        );
      }
    };

  return (
    <group ref={groupRef} name="note-fragments">
      {noteConfigs.map(({ note, tilt }, i) => (
        <group
          key={note.id}
          name={`note-fragment-${note.id}`}
          userData={{ kind: 'note', id: note.id }}
        >
          <group
            ref={(node) => {
              billboardRefs.current[i] = node;
            }}
            name={`note-billboard-${note.id}`}
            rotation={[tilt[0], tilt[1], tilt[2]]}
          >
            <mesh
              ref={(node) => {
                meshRefs.current[i] = node;
              }}
              name={`note-mesh-${note.id}`}
              onPointerOver={handlePointerOver(note, i)}
              onPointerOut={handlePointerOut(note, i)}
              onClick={handleClick(note, i)}
              userData={{ kind: 'note', id: note.id }}
              frustumCulled
            >
              <boxGeometry args={[0.18, 0.24, 0.04]} />
              <meshStandardMaterial
                color={'#f5f0e8'}
                emissive={'#f5f0e8'}
                emissiveIntensity={note.positionOnOrbit.sphereRadius > FAR_SIMPLIFY_RADIUS ? BASE_EMISSIVE * 0.6 : BASE_EMISSIVE}
                roughness={0.55}
                metalness={0.0}
                flatShading
                transparent
                opacity={0.95}
              />
            </mesh>

            {/* Tiny gold spine strip on the left edge (decorative).
             *  Suppressed entirely for notes past the simplify
             *  radius — they're far enough out that the spine is a
             *  single-pixel decoration anyway, and skipping it cuts
             *  one mesh + one material from the draw list. */}
            {note.positionOnOrbit.sphereRadius <= FAR_SIMPLIFY_RADIUS && (
              <mesh
                position={[-0.095, 0, 0]}
                name={`note-spine-${note.id}`}
                raycast={() => null}
                frustumCulled
              >
                <boxGeometry args={[0.012, 0.24, 0.04]} />
                <meshStandardMaterial
                  color={'#b8a572'}
                  emissive={'#b8a572'}
                  emissiveIntensity={0.15}
                  roughness={0.6}
                />
              </mesh>
            )}

            {/* Hover-only title label.
                NOTE: Drei <Html> was removed (Phase 14 regression —
                reads lod.levels before ref is attached under Suspense).
                billboardRefs still tracks the world position so a
                future CSS2D-style overlay can subscribe. */}
          </group>
        </group>
      ))}
    </group>
  );
}

export default NoteFragments;
